/**
 * Exchange-rate precision and the table-rate rules an AP payment's quote rows follow (#5135, #5359).
 *
 * **A mirror, not the authority.** `CurrencyConverter.RateDecimals` in the API is the real precision and
 * `ResolveSettlementRatesAsync` is where a blank rate is defaulted — these exist so the screen previews the
 * cash and the rate the API will actually post, to the cent, instead of a close neighbour of it.
 */

/** Decimal places a rate is kept to — `CurrencyConverter.RateDecimals` on the API. Every rate input and
 *  every multiplier the screen computes uses this, so a bank quote like 27.0173544973 is kept, not cut. */
export const RATE_DECIMALS = 10;

/** A units-per-USD quote as the → functional (USD) multiplier the math runs on, rounded like
 *  `CurrencyConverter.CrossRate`. Zero when there is no usable quote — a row nobody can price yet. */
export function toFunctionalMultiplier(rateToUsd: number): number {
  const scale = 10 ** RATE_DECIMALS;
  return rateToUsd > 0 ? Math.round((1 / rateToUsd) * scale) / scale : 0;
}

/** The date a quote row's table rate is read on: the payment's own date, as the API defaults it
 *  (GetRateToUsdAsync on PaymentDate) — never simply today, or a backdated payment previews one rate and
 *  posts another. Today only while the date is still blank. */
export function tableRateDate(paymentDate: Date | null | undefined): Date {
  return paymentDate ?? new Date();
}

/** True when a translation row should take a freshly read table rate: it is still blank, or it still
 *  holds the table rate it was prefilled with — it was following the table, not overridden. A rate the
 *  user typed is never replaced. */
export function followsTableRate(current: number | null | undefined, previousTableRate: number | null | undefined): boolean {
  const value = current ?? 0;
  return value <= 0 || (previousTableRate != null && value === previousTableRate);
}
